import { Divider, Tab, Tabs } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import * as React from "react";
import { FC, Fragment, useCallback, useEffect, useState } from "react";
import {
  BooleanField,
  Datagrid,
  Filter,
  FilterProps,
  Identifier,
  List,
  ListContextProvider,
  ListProps,
  ReferenceField,
  ReferenceInput,
  SelectInput,
  TextField,
  TextInput,
  useGetList,
  useListContext,
} from "react-admin";
import { CustomDateField } from "../../../Components/CustomDateField";
import { CustomDatetime } from "../../../Components/CustomDatetime";
import CreatedByField from "../../../Components/ReferenceFields/CreatedBy/CreatedByField";
import CustomerField from "../../../Components/ReferenceFields/Customer/CustomerField";
import { DefaultCreatedDateSort, DefaultIsActiveFilter, DefaultPerPageSize } from "../../../Constants/Constant";
import { ActivePassiveStatus } from "../../../Enums/Active";
import { ExcelExport } from "../../../Helper/ExportExcel";
import { TabbedDatagridProps } from "../../../Models/TabbedDataGridProps";
import { SourceCustomer } from "../Customer";
import { SourceRemoveReasonTypes } from "../RemovalReasonType";
import { RemovalReasonTypeDetailExcelHeader, RemovalReasonTypeDetailExcelName } from "./Constant/Excel";
import { SourceRemoveReasonTypeDetail, TitleRemoveReasonTypeDetails } from "./Constant/Source";

const RemoveReasonDetailFilter: FC<Omit<FilterProps, "children">> = props => (
  <Filter {...props}>
    <ReferenceInput
      label="CUSTOMER"
      source="customerId"
      reference={SourceCustomer}
      perPage={99999}
      translateChoice={false}
    >
      <SelectInput optionText="customerName" optionValue="id" />
    </ReferenceInput>
    <ReferenceInput
      label="REMOVE REASON TYPE"
      source="removeReasonTypeId"
      perPage={99999}
      reference={SourceRemoveReasonTypes}
      translateChoice={false}
    >
      <SelectInput optionText="removeReasonTypeName" optionValue="id" />
    </ReferenceInput>

    <TextInput label="Removal Reason Detail Code" source="removeReasonDetailCode__contains" alwaysOn />
    <TextInput label="Removal Reason Detail Name" source="removeReasonDetailName__contains" alwaysOn />
    <CustomDatetime source="createdDate__gte" label="Tarih(>=)" />
    <CustomDatetime source="createdDate__lte" label="Tarih (<=)" />
  </Filter>
);

const ExportToExcel = data => {
  ExcelExport(
    RemovalReasonTypeDetailExcelHeader,
    data.map(v => [v.removeReasonDetailCode, v.removeReasonDetailName, v.isRequired, v.createdBy, v.createdDate]),
    RemovalReasonTypeDetailExcelName
  );
};

const useDatagridStyles = makeStyles({
  total: { fontWeight: "bold" },
});

const useGetTotals = (filterValues: any) => {
  const { total: totalIsActive } = useGetList(
    SourceRemoveReasonTypeDetail,
    { perPage: 100, page: 1 },
    { field: "createdDate", order: "DESC" },
    { ...filterValues, isActive__eq: true }
  );
  const { total: totalIsPassive } = useGetList(
    SourceRemoveReasonTypeDetail,
    { perPage: 100, page: 1 },
    { field: "createdDate", order: "DESC" },
    { ...filterValues, isActive__eq: false }
  );

  return {
    active: totalIsActive,
    passive: totalIsPassive,
  };
};

const TabbedDatagrid: FC<TabbedDatagridProps> = props => {
  const listContext = useListContext();
  const { ids, filterValues, setFilters, displayedFilters } = listContext;
  const [isActive, setIsActive] = useState<Identifier[]>([] as Identifier[]);
  const [isPassive, setIsPassive] = useState<Identifier[]>([] as Identifier[]);

  const totals = useGetTotals(filterValues) as any;

  useEffect(() => {
    if (ids && ids !== filterValues.isActive__eq) {
      switch (filterValues.isActive__eq) {
        case true:
          setIsActive(ids);
          break;
        case false:
          setIsPassive(ids);
          break;
      }
    }
  }, [ids, filterValues.isActive__eq]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<{}>, value: any) => {
      setFilters && setFilters({ ...filterValues, isActive__eq: value == 1 }, displayedFilters);
    },
    [displayedFilters, filterValues, setFilters]
  );

  return (
    <Fragment>
      <Tabs
        variant="fullWidth"
        centered
        value={filterValues.isActive__eq ? 1 : 0}
        indicatorColor="primary"
        onChange={handleChange}
      >
        {ActivePassiveStatus.map(choice => (
          <Tab
            key={choice.id}
            label={totals[choice.name] ? `${choice.name} (${totals[choice.name]})` : choice.name}
            value={choice.id}
          />
        ))}
      </Tabs>
      <Divider />

      <ListContextProvider
        value={{
          ...listContext,
          ids: filterValues.isActive_eq ? isActive : isPassive,
        }}
      >
        <Datagrid {...props} optimized rowClick="edit">
          <CustomerField label="CUSTOMER" link="" />
          <ReferenceField
            label="REMOVE REASON TYPE INFO"
            source="removeReasonTypeId"
            reference={SourceRemoveReasonTypes}
            link=""
          >
            <TextField source="removeReasonTypeName" />
          </ReferenceField>
          <TextField source="removeReasonDetailCode" label="REMOVE REASON DETAIL CODE" />
          <TextField source="removeReasonDetailName" label="REMOVE REASON DETAIL NAME" />
          <BooleanField source="isRequired" label="IS REQUIRED" />
          <CreatedByField label="CREATED BY" link={false} />
          <CustomDateField source="createdDate" label="CREATED DATE" showTime />
        </Datagrid>
      </ListContextProvider>
    </Fragment>
  );
};

const RemoveReasonDetailList: FC<ListProps> = props => (
  <List
    {...props}
    title={TitleRemoveReasonTypeDetails.list}
    filterDefaultValues={DefaultIsActiveFilter}
    sort={DefaultCreatedDateSort}
    perPage={DefaultPerPageSize}
    exporter={ExportToExcel}
    filters={<RemoveReasonDetailFilter />}
  >
    <TabbedDatagrid />
  </List>
);

export default RemoveReasonDetailList;
